/**
 * Settings Service
 * Provides methods to interact with the application settings in the database
 */

import { settingsService as remoteSettingsApi } from '../../shared/utils/serviceProvider';
import demoBrowserDbService from '../../online-demo/services/demoBrowserDbService';
import { isCloudDemoRuntime } from '../../shared/utils/publicDemoRuntime';
import { getSaudiDefaultSettings } from '../../shared/config/saudiSettingsDefaults';

function settingsBackend() {
  return isCloudDemoRuntime() ? demoBrowserDbService : remoteSettingsApi;
}

/** Serialize all writes so concurrent field updates cannot overwrite each other. */
let persistChain = Promise.resolve();

/** Run persists in order; failed tasks reject to the caller; queue stays usable. */
function enqueuePersist(task) {
  const queued = persistChain.then(() => task());
  persistChain = queued.catch(() => {});
  return queued;
}

async function persistToApi(settings) {
  const result = await settingsBackend().updateSettings(settings);
  return result || settings;
}

const settingsService = {
  /**
   * Get all application settings
   * @returns {Promise<Object>} Settings object
   */
  get: async () => {
    try {
      console.log('Fetching settings from API...');
      const settings = await settingsBackend().getSettings();
      console.log('Settings received from API:', settings);

      if (!settings || Object.keys(settings).length === 0) {
        console.log('Settings empty, using Saudi defaults');
        return getDefaultSettings();
      }

      return settings;
    } catch (error) {
      console.error('Error getting settings:', error);
      return getDefaultSettings();
    }
  },

  /**
   * Update application settings
   * @param {Object} settings Settings data
   * @returns {Promise<Object>} Updated settings
   */
  update: async (settings) => {
    return enqueuePersist(async () => {
      try {
        console.log('Saving settings to API:', settings);
        console.log('Saving settings.taxes:', JSON.stringify(settings.taxes));
        return await persistToApi(settings);
      } catch (error) {
        console.error('Error updating settings via API:', error);
        throw error;
      }
    });
  },

  /**
   * Update a specific section of settings
   * @param {string} section Section name
   * @param {string} field Field name
   * @param {any} value New value
   * @returns {Promise<Object>} Updated settings
   */
  updateSection: async (section, field, value) => {
    return enqueuePersist(async () => {
      try {
        const current = await settingsService.get();

        if (!current[section]) {
          current[section] = {};
        }

        current[section][field] = value;

        return await persistToApi(current);
      } catch (error) {
        console.error(`Error updating settings section ${section}.${field}:`, error);
        throw error;
      }
    });
  },

  /**
   * Reset settings to defaults
   * @returns {Promise<Object>} Default settings
   */
  reset: async () => {
    const defaultSettings = getDefaultSettings();
    return enqueuePersist(async () => {
      try {
        return await persistToApi(defaultSettings);
      } catch (error) {
        console.error('Error resetting settings:', error);
        throw error;
      }
    });
  },
};

/**
 * Get default settings (Saudi / Qawaem ERP).
 * @returns {Object} Default settings object
 */
function getDefaultSettings() {
  return getSaudiDefaultSettings();
}

export default settingsService;
export { getDefaultSettings };
